import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
  Notification,
  NotificationVariants,
} from "@hid-galaxy-ui/galaxy-react";
import { usePartnerDashboardData } from "./hooks/usePartnerDashboardData";
import DashboardKpiRow from "./components/kpi-row/DashboardKpiRow";
import ContractStatusCard from "./components/contract-status/ContractStatusCard";
import OrdersInvoicesCard from "./components/orders-invoices/OrdersInvoicesCard";
import PendingActionsCard from "./components/pending-actions/PendingActionsCard";
import ProjectStatusSection from "./components/project-status/ProjectStatusSection";
import EventsWeekCard from "./components/events-week/EventsWeekCard";
import EscalationTrackerCard from "./components/escalation-tracker/EscalationTrackerCard";
import EndCustomerEcosystemSection from "./components/end-customer-ecosystem/EndCustomerEcosystemSection";
import PartnerProgramCard from "./components/partner-program/PartnerProgramCard";
import PerformanceTrendSection from "./components/performance-trend/PerformanceTrendSection";
import {
  DASHBOARD_BLOCKS,
  DashboardVariant,
  RightColumnBlockId,
} from "./config/dashboardBlocks";
import { STATIC_DASHBOARD_BLOCKS_DISABLED } from "./data/staticDashboardData";
import { useDashboardPermissions } from "./hooks/useDashboardPermissions";
import { useIsPartnerProgramEligible } from "../../hooks/useIsPartnerProgramEligible";
import { useAppDispatch, useAppSelector } from "../../redux";
import { partnerDashboardSliceAction } from "../../redux/slices/partner-dashboard";
import "./PartnerDashboard.scss";

/** Admin KPI card → manage-partner sidebar tab keys (index-aligned). */
const ADMIN_KPI_TAB_KEYS = [
  "partner-details",
  "services-and-subscriptions",
  "partner-program",
  "project-onboarding",
] as const;

export type PartnerDashboardViewProps = {
  partnerId: number;
  variant: DashboardVariant;
  headerSlot?: React.ReactNode;
};

const PartnerDashboardView: React.FC<PartnerDashboardViewProps> = ({
  partnerId,
  variant,
  headerSlot,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [toasts, setToasts] = React.useState<any[]>([]);
  const { contractReminderStatus, contractReminderMessage } = useAppSelector(
    (state) => state.partner_dashboard.status
  );
  const data = usePartnerDashboardData(partnerId);
  const blocks = DASHBOARD_BLOCKS[variant];
  const { canEditSupportStatus, viewAllOrdersEnabled, viewAllProjectsEnabled } =
    useDashboardPermissions(variant);
  const isPartnerProgramEligible = useIsPartnerProgramEligible();

  const handleAdminKpiClick = React.useCallback(
    (index: number) => {
      if (variant !== "admin") return;
      const tabKey = ADMIN_KPI_TAB_KEYS[index];
      if (!tabKey) return;
      navigate(
        `/hid-admin/dashboard/partner-organizations/${partnerId}/${tabKey}`
      );
    },
    [navigate, partnerId, variant]
  );

  React.useEffect(() => {
    if (
      contractReminderStatus === "idle" ||
      !contractReminderMessage
    ) {
      return;
    }

    const isSuccess = contractReminderStatus === "success";
    setToasts([
      {
        id: Date.now(),
        title: isSuccess ? "Reminder Sent" : "Error",
        message: contractReminderMessage,
        variant: isSuccess
          ? NotificationVariants.Success
          : NotificationVariants.Error,
        icon: (
          <Iconography
            icon={isSuccess ? "circleCheck" : "ban"}
            size={IconographySizes.Medium}
          />
        ),
        expanded: true,
      },
    ]);
    dispatch(partnerDashboardSliceAction.resetContractReminderStatus());
  }, [contractReminderStatus, contractReminderMessage, dispatch]);

  if (data.isLoading) {
    return (
      <div
        className={`partner-dashboard partner-dashboard--${variant} partner-dashboard--loading`}
      >
        <Loader variant={LoaderSizes.large} />
      </div>
    );
  }

  const renderRightColumnBlock = (blockId: RightColumnBlockId) => {
    switch (blockId) {
      case "partnerProgram":
        return blocks.showPartnerProgram && isPartnerProgramEligible ? (
          <PartnerProgramCard
            key={blockId}
            data={data.partnerProgram}
            partnerId={partnerId}
          />
        ) : null;
      case "pendingActions":
        return (
          <PendingActionsCard
            key={blockId}
            actions={data.pendingActions}
            openCount={data.openActionsCount}
            isStaticDisabled={STATIC_DASHBOARD_BLOCKS_DISABLED}
          />
        );
      case "eventsWeek":
        return (
          <EventsWeekCard
            key={blockId}
            data={data.eventsWeek}
            isStaticDisabled={STATIC_DASHBOARD_BLOCKS_DISABLED}
          />
        );
      case "escalationTracker":
        return blocks.showEscalationTracker ? (
          <EscalationTrackerCard
            key={blockId}
            data={data.escalations}
            isStaticDisabled={STATIC_DASHBOARD_BLOCKS_DISABLED}
          />
        ) : null;
      default:
        return null;
    }
  };

  return (
    <div className={`partner-dashboard partner-dashboard--${variant}`}>
      <Notification toasts={toasts} setToasts={setToasts} />
      <div className="partner-dashboard__content">
        {headerSlot}

        <DashboardKpiRow
          cards={data.kpiCards}
          onCardClick={variant === "admin" ? handleAdminKpiClick : undefined}
        />

        <div className="partner-dashboard__main-grid">
        <div className="partner-dashboard__left-col">
          <div className="partner-dashboard__top-row">
            <div className="partner-dashboard__contract-col">
              <ContractStatusCard
                data={data.contractStatus}
                supportStatus={data.supportStatus}
                showSupportStatus={blocks.showSupportStatus}
                supportStatusReadOnly={!canEditSupportStatus}
                isSupportStatusSaving={data.isSupportStatusSaving}
                supportStatusError={data.supportStatusError}
                onSaveSupportStatus={data.saveSupportStatus}
                onClearSupportStatusError={data.clearSupportStatusError}
                onSendContractReminder={data.sendContractReminder}
                isContractReminderSending={data.isContractReminderSending}
              />
            </div>
            <div className="partner-dashboard__orders-col">
              <OrdersInvoicesCard
                orders={data.orders}
                showRecentOrdersHeader={variant === "admin"}
                viewAllEnabled={viewAllOrdersEnabled}
              />
            </div>
          </div>
          <div className="partner-dashboard__projects-col">
            <ProjectStatusSection
              projects={data.projects}
              filters={data.projectFilters}
              partnerId={partnerId}
              viewAllEnabled={viewAllProjectsEnabled}
            />
          </div>
        </div>
        <div className="partner-dashboard__right-col">
          {blocks.rightColumnOrder.map(renderRightColumnBlock)}
        </div>
      </div>

      {blocks.showPerformanceTrend && (
        <PerformanceTrendSection
          metrics={data.endCustomerEcosystem.metrics}
          isStaticDisabled={STATIC_DASHBOARD_BLOCKS_DISABLED}
        />
      )}

      {blocks.showEndCustomerEcosystem && (
        <EndCustomerEcosystemSection
          data={data.endCustomerEcosystem}
          isStaticDisabled={STATIC_DASHBOARD_BLOCKS_DISABLED}
        />
      )}
      </div>
    </div>
  );
};

export default PartnerDashboardView;
